// API clients for all Pipenodes backend services.
// Each client reads base URLs from environment variables.

const AUTH_URL        = import.meta.env.VITE_AUTH_URL        ?? 'http://localhost:8100';
const BILLING_URL     = import.meta.env.VITE_BILLING_URL     ?? 'http://localhost:8101';
const MARKETPLACE_URL = import.meta.env.VITE_MARKETPLACE_URL ?? 'http://localhost:8102';
const PIPENODES_URL   = import.meta.env.VITE_PIPENODES_URL   ?? 'http://localhost:8103';

// ── Token storage ──────────────────────────────────────────────────────────────

export const tokenStore = {
  getAccess: () => localStorage.getItem('access_token') ?? '',
  getRefresh: () => localStorage.getItem('refresh_token') ?? '',
  set: (access: string, refresh: string) => {
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
  },
  clear: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },
};

// ── Base fetch with auth ───────────────────────────────────────────────────────

async function request<T>(
  baseUrl: string,
  path: string,
  options: RequestInit = {},
  withAuth = true,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (withAuth) {
    const token = tokenStore.getAccess();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${baseUrl}${path}`, { ...options, headers });

  if (res.status === 401) {
    // Attempt token refresh
    const refreshed = await authApi.refresh();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${tokenStore.getAccess()}`;
      const retry = await fetch(`${baseUrl}${path}`, { ...options, headers });
      if (!retry.ok) throw new ApiError(retry.status, await retry.json());
      return retry.json();
    }
    tokenStore.clear();
    window.location.href = '/auth/login';
    throw new ApiError(401, { error: 'Session expired' });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, body);
  }

  return res.status === 204 ? (undefined as T) : res.json();
}

export class ApiError extends Error {
  constructor(public status: number, public body: { error: string }) {
    super(body.error);
  }
}

// ── Auth API ───────────────────────────────────────────────────────────────────

export interface AuthUser { id: string; email: string }
export interface AuthOrg  { id: string; name: string; slug: string }
export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: AuthUser;
  org?: AuthOrg;
  orgs?: Array<{ id: string; role: string }>;
}

export const authApi = {
  register: (email: string, password: string, org_name?: string) =>
    request<AuthResponse>(AUTH_URL, '/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, org_name }),
    }, false),

  login: (email: string, password: string) =>
    request<AuthResponse>(AUTH_URL, '/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }, false).then((r) => {
      tokenStore.set(r.access_token, r.refresh_token);
      return r;
    }),

  refresh: async (): Promise<boolean> => {
    const refresh_token = tokenStore.getRefresh();
    if (!refresh_token) return false;
    try {
      const r = await request<AuthResponse>(AUTH_URL, '/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refresh_token }),
      }, false);
      tokenStore.set(r.access_token, r.refresh_token);
      return true;
    } catch {
      return false;
    }
  },

  me: () => request<{ id: string; email: string; orgs: Array<{ id: string; role: string }> }>(
    AUTH_URL, '/auth/me'),

  logout: () => request<void>(AUTH_URL, '/auth/logout', { method: 'POST' })
    .finally(() => tokenStore.clear()),

  createOrg: (name: string, slug?: string) =>
    request<AuthOrg>(AUTH_URL, '/auth/orgs', {
      method: 'POST',
      body: JSON.stringify({ name, slug }),
    }),
};

// ── Billing API ────────────────────────────────────────────────────────────────

export interface Plan {
  id: string; name: string; price_cents: number; currency: string; interval: string;
  features: string[];
}

export interface Subscription {
  id?: string; plan_id: string; status: string;
  current_period_end?: number; features: string[];
}

export const billingApi = {
  plans: () => request<{ plans: Plan[] }>(BILLING_URL, '/billing/plans', {}, false),

  subscription: () => request<Subscription>(BILLING_URL, '/billing/subscription'),

  checkout: (plan_id: string) =>
    request<{ session_id: string; checkout_url: string }>(BILLING_URL, '/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan_id }),
    }),

  cancel: () => request<{ message: string }>(BILLING_URL, '/billing/cancel', { method: 'POST' }),

  features: () => request<{ plan_id: string; features: string[] }>(BILLING_URL, '/billing/features'),
};

// ── Marketplace API ────────────────────────────────────────────────────────────

export interface Plugin {
  name: string; version: string; product: string; tier: string;
  description: string; min_core_version: string; max_core_version: string;
  accessible: boolean;
}

export interface PluginInstall {
  name: string; version: string; product: string; installed_at: string;
}

export const marketplaceApi = {
  plugins: (params?: { product?: string; tier?: string; core_version?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>);
    return request<{ plugins: Plugin[]; total: number }>(
      MARKETPLACE_URL, `/marketplace/plugins?${q}`);
  },

  install: (name: string, version?: string, product?: string, core_version?: string) =>
    request<{ plugin: string; version: string; download_url: string; checksum_sha256: string }>(
      MARKETPLACE_URL, `/marketplace/plugins/${name}/install`, {
        method: 'POST',
        body: JSON.stringify({ version, product, core_version }),
      }),

  checkUpdates: (product: string, core_version: string, installed: Array<{ name: string; version: string }>) =>
    request<{ updates: Array<{ name: string; current_version: string; new_version: string; download_url: string }> }>(
      MARKETPLACE_URL, '/marketplace/updates/check', {
        method: 'POST',
        body: JSON.stringify({ product, core_version, installed }),
      }),

  installs: () => request<{ installs: PluginInstall[] }>(MARKETPLACE_URL, '/marketplace/installs'),
};

// ── Pipenodes API ──────────────────────────────────────────────────────────────

export type DeployMode = 'managed' | 'self_hosted' | 'private_cloud';
export type ProductId  = 'llm-gateway' | 'audit-gateway' | 'log-gateway';

export interface Deployment {
  id: string; product: ProductId; deploy_mode: DeployMode;
  name: string; region?: string; endpoint_url?: string;
  core_version?: string; status: string; created_at: number;
}

export interface LicenseInfo {
  plan_id: string; deploy_mode: string; features: string[];
  issued_at: number; expires_at: number; revoked: boolean; last_seen_at: number;
}

export const pipenodesApi = {
  deployments: () => request<{ deployments: Deployment[] }>(PIPENODES_URL, '/deployments'),

  createDeployment: (data: {
    product: ProductId;
    deploy_mode: DeployMode;
    name: string;
    region?: string;
    endpoint_url?: string;
  }) => request<Deployment>(PIPENODES_URL, '/deployments', {
    method: 'POST', body: JSON.stringify(data),
  }),

  deploymentHealth: (id: string) =>
    request<{ deployment_id: string; checks: Array<{ status: string; latency_ms: number; checked_at: number }> }>(
      PIPENODES_URL, `/deployments/${id}/health`),

  updateEndpoint: (id: string, endpoint_url: string) =>
    request<{ ok: boolean }>(PIPENODES_URL, `/deployments/${id}/endpoint`, {
      method: 'PUT', body: JSON.stringify({ endpoint_url }),
    }),

  removeDeployment: (id: string) =>
    request<{ ok: boolean }>(PIPENODES_URL, `/deployments/${id}`, { method: 'DELETE' }),

  licenses: () => request<{ licenses: LicenseInfo[] }>(PIPENODES_URL, '/licenses'),

  validateLicense: (license_key: string) =>
    request<{ valid: boolean; org_id?: string; plan_id?: string; features?: string[] }>(
      PIPENODES_URL, '/licenses/validate', {
        method: 'POST', body: JSON.stringify({ license_key }),
      }, false),
};
